<script setup lang="ts">
import WrongMessage from '@/components/login/errorAlert/WrongMessage.vue';
import WrongSex from '@/components/login/errorAlert/WrongSex.vue';
import WrongSignupPhone from '@/components/login/errorAlert/WrongSignupPhone.vue';
import { RuleEnum } from '@/constants/enums/RuleEnum';
import type { ThirdPartyLogin } from '@/models/interface/auth';
import { api } from '@/services';
import { isApiSuccess } from '@/utils/api/functions';
import FormCard from './formElement/FormCard.vue';
import FormInput from './formElement/FormInput.vue';

const formChoose = defineModel('formChoose');
const showPopup = defineModel('showPopup');
const showAlert = defineModel('showAlert');
const dialogFormVisible = defineModel('dialogFormVisible');
const information = defineModel<ThirdPartyLogin>('information', { required: true });

const inputRefPhone = ref<InstanceType<typeof FormInput>>();
const inputRefName = ref<InstanceType<typeof FormInput>>();
const { openToast } = useToast();

const sendVerifyCode = async () => {
  const res = await api.auth.sendSmsVerify({
    phone: information.value.phone
  });

  if (isApiSuccess(res)) {
    formChoose.value = 'ThirdPartSMSVerification';
    return;
  }

  if (res.data.errorCode) {
    switch (res.data.errorCode) {
      case -4037:
        showPopup.value = true;
        showAlert.value = WrongMessage;
        break;
      case -4020:
        showPopup.value = true;
        showAlert.value = WrongSignupPhone;
        break;
      case -4038:
        showPopup.value = true;
        showAlert.value = WrongSignupPhone;
        break;
      case -4062:
        showPopup.value = true;
        showAlert.value = WrongSignupPhone;
        break;
      default:
        break;
    }
    openToast('error', res.data.errorMessage ?? '');
  }
};

const thirdPartySignup = async () => {
  const checkedPhone = inputRefPhone.value?.handleBlur() ?? false;
  const checkedName = inputRefName.value?.handleBlur() ?? false;

  if (!checkedPhone || !checkedName) return;

  if (information.value.sex === -1) {
    showPopup.value = true;
    showAlert.value = WrongSex;
    return;
  }
  sendVerifyCode();
};

const openServiceContract = () => {
  window.open('https://funday.asia/policy/policy.html', '_blank');
};

const handleClose = () => {
  formChoose.value = ''
  dialogFormVisible.value = false
}
</script>

<template>
  <FormCard>
    <div class="header-container">
      <div class="header-content">
        <h3>輸入基本資料</h3>
        <SvgIcon name="cross" class="close" @click="handleClose" />
      </div>

      <p>屬於你的 VIP 顧問，陪你一起找到最適合的學習方法。</p>
    </div>

    <div class="email-block">
      <FormInput
        ref="inputRefPhone"
        inputPlaceHolder="手機號碼即您的密碼"
        inputTitle="手機"
        inputType="text"
        :verifyRule="RuleEnum.Phone"
        :required="true"
        v-model:inputValue="information.phone"
      />
    </div>

    <div class="name-and-sex-block">
      <FormInput
        ref="inputRefName"
        inputPlaceHolder="請輸入中文姓名"
        inputType="text"
        inputTitle="中文姓名"
        :required="true"
        :verifyRule="RuleEnum.Name"
        v-model:inputValue="information.name"
      />
      <div class="sex-block">
        <p class="sex">性別<span>*</span></p>
        <div class="choose-sex-block">
          <label class="female-female">
            <input type="radio" value="1" v-model="information.sex" />
            <div class="virtual-input"></div>
            <p>男</p>
          </label>
          <label class="female-female">
            <input type="radio" value="0" v-model="information.sex" />
            <div class="virtual-input"></div>
            <p>女</p>
          </label>
        </div>
      </div>
    </div>

    <div class="login-btn" @click="thirdPartySignup">下一步</div>
    <p class="hint-rule-notice" @click="openServiceContract">
      加入即代表同意FUNDAY的<span>服務條款與隱私權</span>
    </p>
  </FormCard>
</template>

<style scoped lang="scss">
.header-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  width: 100%;
  margin-bottom: 16px;

  .header-content {
    display: flex;
    align-items: center;
    width: 100%;

    h3 {
      font-weight: 600;
      font-size: 18px;
      line-height: 140%;
      letter-spacing: 0px;
      color: #191919cc;
      margin: auto;
    }
  }

  p {
    font-weight: 400;
    font-size: 12px;
    line-height: 150%;
    letter-spacing: 0px;
    color: #00000066;
  }

  .close {
    cursor: pointer;
    color: #19191966;

    &:hover {
      opacity: 0.8;
    }
  }
}
.hint-block {
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 12px;
  gap: 8px;
  margin: 4px 0 16px 0;
  .hint-que {
    color: #00000066;
  }
  .hint-join {
    color: #f74768;
    cursor: pointer;
    &:hover {
      text-decoration: underline;
      text-decoration-color: #f74768;
    }
  }
}
.email-block {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  .send-verify-code {
    border-radius: 4px;
    margin-left: 8px;
    width: 102px;
    flex-shrink: 0;
    white-space: nowrap;
    height: 48px;
    color: $txt-color-grey-80;
    font-size: 14px;
    background-color: #f4f4f4;
    border: none;
    cursor: pointer;
  }
}
.verify-hint {
  color: $txt-color-grey-24;
  font-size: 12px;
  width: 100%;
  margin-top: 4px;
}
.third-part-block {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 16px;
  .virtual-line {
    height: 1px;
    flex: 1;
    border: 1px solid #0000001a;
  }
  p {
    color: #00000066;
    font-size: 12px;
  }
}
.name-and-sex-block {
  display: flex;
  width: 100%;
  justify-content: space-between;
  gap: 16px;
  .sex-block {
    display: flex;
    flex-direction: column;
    white-space: nowrap;
    .sex {
      min-width: 104px;
      color: $txt-color-grey-80;
      font-weight: 600;
      span {
        color: #f74768;
      }
    }
    .choose-sex-block {
      display: flex;
      justify-content: space-between;

      .female-female {
        display: flex;
        height: 48px;
        align-items: center;
        color: $txt-color-grey-60;
        cursor: pointer;
        input {
          display: none;
        }
        .virtual-input {
          width: 24px;
          height: 24px;
          background-position: center center;
          background-repeat: no-repeat;
          margin-right: 4px;
          background-image: url('../../assets/icon/inputRadio.svg');
          &:hover {
            background-image: url('../../assets/icon/inputRadioHover.svg');
          }
        }
        input:checked ~ .virtual-input {
          background-image: url('../../assets/icon/inputRadioChecked.svg');
          &:hover {
            background-image: url('../../assets/icon/inputRadioCheckedAndHover.svg');
          }
        }
      }
    }
  }
}
.login-btn {
  margin: 16px 0;
  background-color: #f74768;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  color: #ffffff;
  height: 40px;
  width: 100%;
  cursor: pointer;
}
.third-part-icon-block {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
}
.hint-rule-notice {
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 12px;
  color: $txt-color-grey-40;
  font-size: 12px;
  margin-top: 16px;
  span {
    position: relative;
    cursor: pointer;
    &:after {
      content: '';
      width: 100%;
      bottom: -2px;
      left: 0;
      height: 1px;
      background-color: $txt-color-grey-40;
      position: absolute;
    }
  }
}
</style>
